import type { ColumnPinningState } from '@tanstack/react-table'
import { useRef } from 'react'
import { type DataTableSelectionConfig, SELECT_COLUMN_ID } from './types'

/** Pin the injected column left on top of the parent-owned pinning state. */
export function withSelectPinning(
	pinning: ColumnPinningState
): ColumnPinningState {
	return {
		left: [SELECT_COLUMN_ID, ...(pinning.left ?? [])],
		right: pinning.right
	}
}

function applyToSet(set: Set<string>, id: string, checked: boolean) {
	if (checked) {
		set.add(id)
	} else {
		set.delete(id)
	}
}

/** Ids touched by a click: the single row, or the anchor→row range on shift-click. */
function rowIdsForToggle<TData extends { id: string }>(args: {
	canSelectRow: (row: TData) => boolean
	data: TData[]
	anchorId: string | null
	rowId: string
	shiftKey: boolean
}) {
	const { canSelectRow, data, anchorId, rowId, shiftKey } = args
	const from =
		shiftKey && anchorId !== null
			? data.findIndex((row) => row.id === anchorId)
			: -1
	const to = from === -1 ? -1 : data.findIndex((row) => row.id === rowId)
	if (from === -1 || to === -1) {
		return [rowId]
	}
	return data
		.slice(Math.min(from, to), Math.max(from, to) + 1)
		.filter(canSelectRow)
		.map((row) => row.id)
}

/**
 * Owns the DataTable row-selection logic. Toggle handlers are plain closures
 * over the current `selection`/`data`; a toggle produces a fresh `selectedIds`
 * set, so the visible (virtualized) rows re-render with the new selection.
 */
export function useDataTableSelection<TData extends { id: string }>(args: {
	selection: DataTableSelectionConfig<TData> | undefined
	data: TData[]
	selectRowLabel: string
	selectAllLabel: string
}) {
	const { selection, data } = args
	const anchorIdRef = useRef<string | null>(null)

	if (!selection) {
		return { headerSelectAll: undefined, bodySelection: undefined }
	}

	const canSelectRow = selection.isRowSelectable ?? (() => true)

	const onToggleRow = (
		rowId: string,
		checked: boolean,
		shiftKey: boolean
	) => {
		const next = new Set(selection.selectedIds)
		for (const id of rowIdsForToggle({
			canSelectRow,
			data,
			anchorId: anchorIdRef.current,
			rowId,
			shiftKey
		})) {
			applyToSet(next, id, checked)
		}
		anchorIdRef.current = rowId
		selection.onSelectionChange(next)
	}

	const selectableIds = data.filter(canSelectRow).map((row) => row.id)
	const selectedCount = selectableIds.filter((id) =>
		selection.selectedIds.has(id)
	).length
	const allSelected =
		selectableIds.length > 0 && selectedCount === selectableIds.length

	const headerSelectAll =
		selection.enableSelectAll === false
			? undefined
			: {
					checked: allSelected,
					indeterminate: selectedCount > 0 && !allSelected,
					disabled: selectableIds.length === 0,
					ariaLabel: args.selectAllLabel,
					onCheckedChange: (checked: boolean) => {
						const next = new Set(selection.selectedIds)
						for (const id of selectableIds) {
							applyToSet(next, id, checked)
						}
						anchorIdRef.current = null
						selection.onSelectionChange(next)
					}
				}

	const bodySelection = {
		selectedIds: selection.selectedIds,
		isRowSelectable: selection.isRowSelectable,
		notSelectableTooltip: selection.notSelectableTooltip,
		onToggleRow,
		rowAriaLabel: args.selectRowLabel
	}

	return { headerSelectAll, bodySelection }
}
